'use client';

import { usePathname } from 'next/navigation';
import Link from 'next/link';
import {
  useIdentityService,
  useUserData
} from '../../../providers/IdentityProvider';
import { useLoading } from '../../../providers/LoadingProvider';
import { useLineChatChannelState } from '../../../providers/ChatMessagesProvider/LineChatMessagingProvider';
import { useEffect, useRef, useState } from 'react';
import {
  StyledSideBottomList,
  StyledSidePanel,
  StyledSideTopList,
  StyledSideItem,
  StyledMainPanel,
  StyledSideSubItemList,
  StyledSideSubItem
} from './Styled';
import Dashboard from '../../icons/Dashboard';
import MessageDots from '../../icons/MessageDots';
import Staff from '../../icons/Staff';
import Setting from '../../icons/Setting';
import AngleRight from '../../icons/AngleRight';
import { useAutoModal } from '../../common/AutoModal/Context';
import classnames from 'classnames';
import useClickOutside from '../../../hooks/useClickOutside';
import useTabOutside from '../../../hooks/useTabOutside';
import useHover from '../../../hooks/useHover';
import { signOut } from 'next-auth/react';
import { useMember } from '../../../providers/SessionProvider';
import { ConnectUserType } from '../../../constants';
import { getTermTitleText } from '../../../utils/get-text.utils';
import { isSupportedFcm, updatePushToken } from '../../../utils/firebase.utils';

const SidePanel = ({ page, showSidePanelMenu, onClickOverlay }) => {
  const { identityClient, useCompany } = useIdentityService();
  const { channelList } = useLineChatChannelState();
  const { userData } = useUserData();
  const [hasUnreadLineMsg, setHasUnreadLineMsg] = useState();
  const pathname = usePathname();
  const { setLoading } = useLoading();
  const { data: company } = useCompany(true);
  const { member } = useMember();
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    if (['/inquiry/message'].some((path) => pathname?.startsWith(path))) {
      setIsFullscreen(true);
      document.body.classList.add('scroll-lock', 'fullscreen');
    } else {
      setIsFullscreen(false);
      document.body.classList.remove('scroll-lock', 'fullscreen');
    }

    return () => {
      document.body.classList.remove('scroll-lock', 'fullscreen');
    };
  }, [pathname]);

  useEffect(() => {
    setHasUnreadLineMsg(
      (channelList || [])?.reduce(
        (prev, curr) =>
          prev ||
          !userData?.channels?.[curr.Metadata.UserId]?.readMarker ||
          (userData?.channels?.[curr.Metadata.UserId]?.readMarker || 0) <
            (curr.LastMessageTimestamp?.getTime() || 0),
        false
      )
    );
  }, [channelList, userData]);

  useEffect(() => {
    document.body.style.overflow = showSidePanelMenu ? 'hidden' : '';
  }, [showSidePanelMenu]);

  return (
    <>
      <StyledSidePanel open={showSidePanelMenu}>
        <StyledSideTopList>
          {
            <>
              <Item
                text="ダッシュボード"
                url="/dashboard"
                Icon={Dashboard}
                childrenUrl={['/info']}
              />
              {/* <Item text="チャット" url="/chat" showBadgeIcon={unreadChannels} Icon={Chat} /> */}
              {company?.isEnableLine && (
                <Item
                  text="メッセージ"
                  url="/inquiry/message"
                  showBadgeIcon={hasUnreadLineMsg}
                  Icon={MessageDots}
                />
              )}
            </>
          }
        </StyledSideTopList>
        <StyledSideBottomList>
          {/* <Item text="ヘルプ" url="/help" Icon={Help} /> */}
          {
            <>
              {identityClient.isAdmin && (
                <Item
                  text="管理メニュー"
                  url=""
                  Icon={Staff}
                  subItemList={[
                    { url: '/company/setting', text: '店舗アカウント管理' },
                    { url: '/service/setting', text: 'サービス管理' },
                    { url: '/account/list', text: 'スタッフ・ユーザ管理' }
                  ]}
                  childrenUrl={['/account', '/company']}
                  notChildrenUrl={['/account/setting']}
                />
              )}
              <Item
                text="設定"
                url=""
                Icon={Setting}
                subItemList={[
                  {
                    url: '/account/setting',
                    text: '個人設定'
                  },
                  ...(member.userType === ConnectUserType.Estate &&
                  company?.isEnableLine
                    ? [
                        {
                          url: '/apps',
                          text: 'アプリ利用'
                        }
                      ]
                    : []),
                  {
                    url: '/service-kiyaku',
                    text: getTermTitleText(member.userType)
                  },
                  {
                    url: '',
                    text: 'ログアウト',
                    onClick: () => {
                      if (window.confirm('ログアウトしてもよろしいですか？')) {
                        setLoading(true);
                        if (
                          isSupportedFcm() &&
                          Notification.permission === 'granted'
                        ) {
                          // fcm のトークン取得と、Dyanamoへ通知OFFの指定を設定する
                          try {
                            updatePushToken(false, identityClient);
                          } catch (e) {
                            console.error({ e });
                          }
                        }
                        signOut({ callbackUrl: '/login/hub' }).finally(() => {
                          setLoading(false);
                        });
                      }
                    }
                  }
                ].filter(Boolean)}
              />
            </>
          }
        </StyledSideBottomList>
      </StyledSidePanel>

      <StyledMainPanel
        className={classnames({
          fullscreen: isFullscreen
        })}
      >
        {showSidePanelMenu && (
          <div className="overlay" onClick={onClickOverlay} />
        )}
        {page}
      </StyledMainPanel>
    </>
  );
};

const Item = ({
  text,
  url,
  showBadgeIcon,
  Icon,
  subItemList,
  childrenUrl = [],
  notChildrenUrl = [],
  onClickSameUrl
}) => {
  const pathname = usePathname();
  const modalEventBus = useAutoModal();
  const itemRef = useRef(null);
  const [openSubItem, setOpenSubItem] = useState(false);

  useHover(
    itemRef,
    () => setOpenSubItem(true),
    () => setOpenSubItem(false)
  );
  useClickOutside(itemRef, () => setOpenSubItem(false));
  useTabOutside(itemRef, () => setOpenSubItem(false));

  return (
    <StyledSideItem ref={itemRef} openSubItem={openSubItem}>
      <Link
        href={url}
        onClick={(e) => {
          url && modalEventBus?.emit('close');
          !url && e.preventDefault();
          setOpenSubItem(!!subItemList?.length);
          pathname === url && onClickSameUrl?.();
        }}
        className={
          (url && pathname?.startsWith(url)) ||
          subItemList?.some(
            (subItem) => subItem.url && pathname?.startsWith(subItem.url)
          ) ||
          (childrenUrl?.some((childUrl) => pathname?.startsWith(childUrl)) &&
            notChildrenUrl.every(
              (notChildUrl) => !pathname?.startsWith(notChildUrl)
            ))
            ? 'selected'
            : undefined
        }
      >
        <div className={showBadgeIcon ? 'icon-wrap has-badge' : 'icon-wrap'}>
          <Icon />
        </div>
        <span>{text}</span>
        {subItemList?.length && (
          <AngleRight className="angle-icon" width="8px" height="13.7px" />
        )}
      </Link>
      {subItemList?.length && (
        <StyledSideSubItemList>
          {subItemList.map((subItem) => (
            <StyledSideSubItem key={subItem.url}>
              <Link
                href={subItem.url}
                onClick={(e) => {
                  subItem.url && modalEventBus?.emit('close');
                  !subItem.url && e.preventDefault();
                  subItem?.onClick && subItem.onClick();
                  setOpenSubItem(false);
                  pathname === subItem.url &&
                    subItem?.onClickSameUrl &&
                    subItem.onClickSameUrl();
                }}
              >
                <span>{subItem.text}</span>
              </Link>
            </StyledSideSubItem>
          ))}
        </StyledSideSubItemList>
      )}
    </StyledSideItem>
  );
};

export default SidePanel;
